import { Link } from "react-router-dom";
import { LucideBookOpen, LucidePencil, LucideEye, LucideMoreHorizontal } from "lucide-react";
import { getTypeLabelByValue } from "@/constants/reportTypes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const SIZES = {
  lg: {
    outer:       "w-[300px]",
    row:         "px-5 pt-4 pb-2 gap-4",
    badge:       "min-w-[60px] px-3 py-2",
    badgeLabel:  "text-[8px]",
    badgeNum:    "text-3xl",
    iconSize:    20,
    name:        "text-base",
    reportBadge: "text-[11px] px-2 py-0.5 mt-1.5",
    footer:      "px-5 py-1",
    footerText:  "text-[13px]",
    permIcon:    16,
    triggerSize: 32,
  },
  md: {
    outer:       "w-[260px]",
    row:         "px-4 pt-3 pb-1.5 gap-3",
    badge:       "min-w-[50px] px-2.5 py-1.5",
    badgeLabel:  "text-[7px]",
    badgeNum:    "text-2xl",
    iconSize:    16,
    name:        "text-sm",
    reportBadge: "text-[10px] px-1.5 py-0.5 mt-1",
    footer:      "px-4 py-0.5",
    footerText:  "text-[11px]",
    permIcon:    14,
    triggerSize: 28,
  },
};

function PermissionIcon({ permission, size }) {
  if (!permission) return null;
  const Icon = permission === "edit" ? LucidePencil : LucideEye;
  return (
    <div className="border border-neutral-200 dark:border-white/25 rounded-md p-1.5 bg-neutral-100 dark:bg-white/10">
      <Icon size={size} className="text-neutral-400 dark:text-white/60" />
    </div>
  );
}

function ActionsMenu({ menuItems, triggerSize }) {
  if (!menuItems?.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          style={{ width: triggerSize, height: triggerSize }}
          className="p-0 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-white/80 dark:hover:text-white dark:hover:bg-white/15 rounded-lg transition-colors"
        >
          <LucideMoreHorizontal size={triggerSize - 12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {menuItems.map(({ label, icon: Icon, onClick, destructive }) => (
          <DropdownMenuItem
            key={label}
            onClick={onClick}
            className={destructive ? "text-destructive focus:text-destructive focus:bg-destructive/10" : ""}
          >
            {Icon && <Icon size={14} className="mr-2" />}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HorizontalCard({ item, menuItems, ownerName, permission, cfg, reportLabel }) {
  return (
    <div className="bg-white from-brand-700 to-brand-800 dark:bg-gradient-to-r flex flex-col">
      <div className={`flex items-center ${cfg.row}`}>
        {item.grade ? (
          <div className={`bg-brand-50 border border-brand-200 dark:bg-white/95 dark:border-transparent rounded-xl text-center shrink-0 shadow-sm ${cfg.badge}`}>
            <p className={`font-bold uppercase tracking-widest text-brand-600 dark:text-neutral-400 leading-none mb-1 ${cfg.badgeLabel}`}>
              Grade
            </p>
            <p className={`font-bold text-brand-700 dark:text-neutral-900 leading-none ${cfg.badgeNum}`}>
              {item.grade}
            </p>
          </div>
        ) : (
          <div className="bg-brand-50 dark:bg-white/20 rounded-xl p-2.5 shrink-0">
            <LucideBookOpen size={cfg.iconSize} className="text-brand-600 dark:text-white" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-neutral-900 dark:text-white leading-snug truncate ${cfg.name}`}>
            {item.name || "Unnamed Report Folder"}
          </p>
          {reportLabel && (
            <span className={`inline-block font-medium text-neutral-500 dark:text-white/70 bg-neutral-100 dark:bg-white/10 border border-neutral-200 dark:border-white/20 rounded-md ${cfg.reportBadge}`}>
              {reportLabel}
            </span>
          )}
          {item.schoolYear && (
            <p className="text-[11px] text-neutral-400 dark:text-white/50 font-normal mt-0.5 truncate">
              {item.schoolYear}
            </p>
          )}
        </div>

        <div
          className="shrink-0 z-10 flex flex-col items-center gap-1"
          onClick={(e) => e.preventDefault()}
        >
          <ActionsMenu menuItems={menuItems} triggerSize={cfg.triggerSize} />
          <PermissionIcon permission={permission} size={cfg.permIcon} />
        </div>
      </div>

      {ownerName && (
        <div className={`bg-neutral-50 border-t border-neutral-200 dark:bg-black/20 dark:border-t-transparent text-center ${cfg.footer}`}>
          <span className={`text-neutral-500 dark:text-white/70 font-medium ${cfg.footerText}`}>
            {ownerName}
          </span>
        </div>
      )}
    </div>
  );
}

function SmallCard({ item, menuItems, ownerName, permission, reportLabel }) {
  return (
    <div className="bg-white from-brand-700 to-brand-800 dark:bg-gradient-to-b flex flex-col">
      <div
        className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        onClick={(e) => e.preventDefault()}
      >
        <ActionsMenu menuItems={menuItems} triggerSize={22} />
      </div>

      <div className="flex flex-col items-center text-center px-2.5 pt-2.5 pb-1 gap-1.5">
        <div className="flex items-center gap-1.5">
          {item.grade ? (
            <div className="bg-brand-50 border border-brand-200 dark:bg-white/95 dark:border-transparent rounded-md px-1.5 py-0.5 text-center shadow-sm">
              <p className="text-sm font-bold text-brand-700 dark:text-neutral-900 leading-none">
                {item.grade}
              </p>
            </div>
          ) : (
            <div className="bg-brand-50 dark:bg-white/20 rounded-md p-1">
              <LucideBookOpen size={12} className="text-brand-600 dark:text-white" />
            </div>
          )}
          <PermissionIcon permission={permission} size={12} />
        </div>

        <p className="text-xs font-semibold text-neutral-900 dark:text-white leading-tight line-clamp-2">
          {item.name || "Unnamed Report Folder"}
        </p>

        {reportLabel && (
          <span className="text-[9px] font-medium text-neutral-500 dark:text-white/70 bg-neutral-100 dark:bg-white/10 border border-neutral-200 dark:border-white/20 rounded px-1.5 py-px leading-tight">
            {reportLabel}
          </span>
        )}

        {item.schoolYear && (
          <p className="text-[9px] text-neutral-400 dark:text-white/45 font-normal leading-none">
            {item.schoolYear}
          </p>
        )}
      </div>

      {ownerName && (
        <div className="bg-neutral-50 border-t border-neutral-200 dark:bg-black/20 dark:border-t-transparent px-2 py-0.5 text-center">
          <span className="text-[9px] text-neutral-500 dark:text-white/70 font-medium truncate">
            {ownerName}
          </span>
        </div>
      )}
    </div>
  );
}

export function ReportFolderCard({ item, getLinkPath, getMenuItems, ownerName, permission, size = "lg" }) {
  const reportLabel = item.reportType ? getTypeLabelByValue(item.reportType) : null;
  const cfg = SIZES[size];
  const menuItems = getMenuItems?.(item) ?? [];
  const isSmall = size === "sm";

  return (
    <div
      className={`relative group cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 ${
        isSmall ? "w-[160px] pt-6 px-2.5 pb-2.5" : `${cfg.outer} pt-6 px-2.5 pb-2.5`
      }`}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 drop-shadow-md transition-[filter] duration-150 group-hover:drop-shadow-lg"
      >
        <div className="absolute inset-x-1 top-[40%] -bottom-[3px] rounded-2xl bg-primary/40 dark:bg-white/40" />
        <svg
          viewBox="0 0 300 30"
          preserveAspectRatio="none"
          className="absolute left-0 top-0 h-[30px] w-full text-primary dark:text-white/90"
        >
          <path
            d="M 10 0 H 100 L 120 14 H 284 A 16 16 0 0 1 300 30 H 0 V 10 A 10 10 0 0 1 10 0 Z"
            fill="currentColor"
          />
        </svg>
        <div className="absolute bottom-0 left-0 right-0 top-[30px] rounded-b-2xl bg-primary dark:bg-white/90" />
      </div>
      <Card className="relative rounded-xl overflow-hidden">
        <Link to={getLinkPath(item)} className="block">
          {isSmall ? (
            <SmallCard item={item} menuItems={menuItems} ownerName={ownerName} permission={permission} reportLabel={reportLabel} />
          ) : (
            <HorizontalCard item={item} menuItems={menuItems} ownerName={ownerName} permission={permission} cfg={cfg} reportLabel={reportLabel} />
          )}
        </Link>
      </Card>
    </div>
  );
}
